import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import { getAllWorkItems, deleteWorkItem, archiveWorkItem } from '../../services/api';
import { toast } from 'sonner';
import { Edit, Trash2, Plus, Archive, ArchiveRestore, ArrowLeft } from 'lucide-react';

const AdminWorkList = () => {
  const navigate = useNavigate();
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [archiving, setArchiving] = useState(null);

  useEffect(() => {
    fetchWorkItems();
  }, []);

  const fetchWorkItems = async () => {
    try {
      const data = await getAllWorkItems();
      setWorkItems(data);
    } catch (error) {
      console.error('Error fetching work items:', error);
      toast.error('Failed to load work items');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${title}"?`)) {
      return;
    }

    setDeleting(id);
    try {
      await deleteWorkItem(id);
      toast.success('Work item deleted successfully');
      fetchWorkItems();
    } catch (error) {
      console.error('Error deleting work item:', error);
      toast.error('Failed to delete work item');
    } finally {
      setDeleting(null);
    }
  };

  const handleArchive = async (id, title, isArchived) => {
    setArchiving(id);
    try {
      await archiveWorkItem(id);
      const action = isArchived ? 'unarchived' : 'archived';
      toast.success(`Work item ${action} successfully`);
      fetchWorkItems();
    } catch (error) {
      console.error('Error archiving work item:', error);
      toast.error('Failed to archive work item');
    } finally {
      setArchiving(null);
    }
  };

  const activeItems = workItems.filter(item => !item.archived);
  const archivedItems = workItems.filter(item => item.archived);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
        <AdminHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <AdminHeader />

      <main className="max-w-[1400px] mx-auto px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">BACK</span>
        </button>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-5xl font-black">
            MANAGE WORK
          </h1>
          <Link
            to="/admin/work/new"
            className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-semibold shadow-lg shadow-purple-500/30"
          >
            <Plus size={20} />
            NEW PROJECT
          </Link>
        </div>

        {/* Active Items */}
        <div className="mb-12">
          <h2 className="text-white font-['Poppins'] text-2xl font-bold mb-6">Active Projects</h2>
          <div className="grid gap-6">
            {activeItems.map((item) => (
              <div
                key={item.id}
                className="glass-effect rounded-2xl p-6 flex gap-6 border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-32 h-32 object-cover rounded-xl border-2"
                  style={{ borderColor: item.border_color }}
                />
                
                <div className="flex-1">
                  <h2 className="text-white font-['Poppins'] text-2xl font-bold mb-2">
                    {item.title}
                  </h2>
                  <p className="text-gray-400 mb-2">{item.role}</p>
                  <p className="text-sm text-gray-500">Order: {item.order}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <Link
                    to={`/admin/work/edit/${item.id}`}
                    className="flex items-center gap-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 px-4 py-2 rounded-lg hover:bg-purple-500/30 transition-all text-sm font-medium"
                  >
                    <Edit size={16} />
                    EDIT
                  </Link>
                  <button
                    onClick={() => handleArchive(item.id, item.title, false)}
                    disabled={archiving === item.id}
                    className="flex items-center gap-2 bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 px-4 py-2 rounded-lg hover:bg-yellow-500/30 transition-all text-sm font-medium disabled:opacity-50"
                  >
                    <Archive size={16} />
                    {archiving === item.id ? 'ARCHIVING...' : 'ARCHIVE'}
                  </button>
                  <button
                    onClick={() => handleDelete(item.id, item.title)}
                    disabled={deleting === item.id}
                    className="flex items-center gap-2 bg-red-500/20 text-red-300 border border-red-500/30 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-all text-sm font-medium disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                    {deleting === item.id ? 'DELETING...' : 'DELETE'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {activeItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-xl mb-4">No active work items</p>
              <Link
                to="/admin/work/new"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-semibold shadow-lg shadow-purple-500/30"
              >
                <Plus size={20} />
                CREATE YOUR FIRST PROJECT
              </Link>
            </div>
          )}
        </div>

        {/* Archived Items */}
        {archivedItems.length > 0 && (
          <div>
            <h2 className="text-gray-400 font-['Poppins'] text-2xl font-bold mb-6 flex items-center gap-2">
              <Archive size={24} />
              Archived Projects ({archivedItems.length})
            </h2>
            <div className="grid gap-6">
              {archivedItems.map((item) => (
                <div
                  key={item.id}
                  className="glass-effect rounded-2xl p-6 flex gap-6 border border-white/5 opacity-60 hover:opacity-100 transition-all"
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-32 h-32 object-cover rounded-xl border-2 grayscale"
                    style={{ borderColor: item.border_color }}
                  />
                  
                  <div className="flex-1">
                    <h2 className="text-white font-['Poppins'] text-2xl font-bold mb-2">
                      {item.title}
                    </h2>
                    <p className="text-gray-400 mb-2">{item.role}</p>
                    <p className="text-sm text-gray-500">Order: {item.order}</p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Link
                      to={`/admin/work/edit/${item.id}`}
                      className="flex items-center gap-2 bg-purple-500/20 text-purple-300 border border-purple-500/30 px-4 py-2 rounded-lg hover:bg-purple-500/30 transition-all text-sm font-medium"
                    >
                      <Edit size={16} />
                      EDIT
                    </Link>
                    <button
                      onClick={() => handleArchive(item.id, item.title, true)}
                      disabled={archiving === item.id}
                      className="flex items-center gap-2 bg-green-500/20 text-green-300 border border-green-500/30 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-all text-sm font-medium disabled:opacity-50"
                    >
                      <ArchiveRestore size={16} />
                      {archiving === item.id ? 'RESTORING...' : 'RESTORE'}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.title)}
                      disabled={deleting === item.id}
                      className="flex items-center gap-2 bg-red-500/20 text-red-300 border border-red-500/30 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-all text-sm font-medium disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                      {deleting === item.id ? 'DELETING...' : 'DELETE'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminWorkList;